/* eslint-disable react-hooks/exhaustive-deps */
import { withRouter } from "../_shared/withRouter";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Header from "../template/_common/header";
import SidebarComponent from "../template/admin/sidebarComponent";
import SidebarArrow from "../template/_common/sidebarArrow";
import LmsAssetRenderer from "../template/admin/lmsAssetRenderer";
import LmsQuizRunner from "../template/admin/lmsQuizRunner";
import LmsPreviewPersonaModal from "../template/admin/lmsPreviewPersonaModal";
import { globalAlert, globalLoader } from "../actions/commonActions";
import { logOut } from "../_shared/commonFunction";
import { lmsCoursePreviewApi } from "./_lmsActions";
import { buildPreviewNodes, previewProgress } from "./_lmsHelpers";

const numberOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const quizQuestionCount = (quiz = {}) =>
  numberOrNull(
    quiz.questionCount ??
      quiz.questionsCount ??
      quiz.totalQuestions ??
      quiz.questions?.length
  );

const quizDurationMinutes = (quiz = {}) =>
  numberOrNull(
    quiz.timeLimitMinutes ??
      quiz.estimatedDurationMinutes ??
      quiz.durationMinutes
  );

const quizPassMark = (quiz = {}) => numberOrNull(quiz.passMark);

const assetId = (asset, fallback = "") =>
  asset?._id || asset?.assetId || asset?.id || fallback;

const serviceAreaForAudience = (audience = "") => {
  const v = String(audience || "").toUpperCase();
  if (v === "ESHS") return "ESSEX";
  if (v === "SSHS") return "SUFFOLK";
  return "";
};

const targetPreviewServiceArea = (preview = {}) =>
  String(
    preview?.previewConfig?.serviceArea ||
      serviceAreaForAudience(preview?.previewConfig?.serviceAudience) ||
      ""
  ).toUpperCase();

const filterSupportForArea = (assets = [], area = "") => {
  const target = String(area || "").toUpperCase();
  if (!target) return assets;
  return assets.filter((asset) =>
    String(asset?.serviceArea || asset?.supportLabel || "").toUpperCase() === target
  );
};

const previewNodeIcon = (kind = "") => {
  if (kind.includes("Quiz") || kind === "finalQuiz") return "fa-question-circle";
  if (kind === "sectionVideo") return "fa-video";
  if (kind === "sectionSummary") return "fa-file-pdf";
  if (kind === "support") return "fa-hands-helping";
  if (kind === "completion") return "fa-flag-checkered";
  if (kind === "moduleIntro" || kind === "courseIntro") return "fa-play-circle";
  return "fa-file-lines";
};

const moduleNodeTitle = (node, t) =>
  node.moduleIndex
    ? t("learner.lms.player.moduleNum", "Module {{n}}", {
        n: node.moduleIndex,
      }) + (node.moduleTitle ? `: ${node.moduleTitle}` : "")
    : "";

export const expandPreviewNode = (node, t) => {
  if (node.kind === "courseIntro") {
    return [{
      ...node,
      short: t("learner.lms.player.courseIntro", "Course introduction"),
      assets: node.asset ? [node.asset] : node.assets || [],
    }];
  }

  if (node.kind === "moduleIntro") {
    return [{
      ...node,
      short: t("learner.lms.player.intro", "Introduction"),
    }];
  }

  if (node.kind !== "section") {
    if (node.kind === "completion") {
      return [{ ...node, short: t("learner.lms.player.completion", "Key-Tips") }];
    }
    if (node.kind === "support") {
      return [{ ...node, short: t("learner.lms.player.support", "Support services") }];
    }
    if (node.kind === "finalQuiz") {
      return [{ ...node, short: t("learner.lms.player.finalAssessment", "Final assessment") }];
    }
    return [node];
  }

  const partTitle =
    node.sectionTitle || t("learner.lms.player.partN", "Part {{n}}", { n: node.partIndex });
  const videoAssets = (node.assets || []).filter((asset) => asset?.type === "video");
  const summaryAssets = (node.assets || []).filter((asset) => asset?.type === "pdf");
  const expanded = [];

  videoAssets.forEach((asset, index) => {
    const videoLabel =
      videoAssets.length > 1
        ? `${t("learner.lms.player.video", "Video")} ${index + 1}`
        : t("learner.lms.player.video", "Video");
    expanded.push({
      ...node,
      key: `${node.key}-video-${assetId(asset, index)}`,
      kind: "sectionVideo",
      short: `${partTitle} - ${videoLabel}`,
      assets: [asset],
      quiz: null,
      quizId: null,
    });
  });

  summaryAssets.forEach((asset, index) => {
    const summaryLabel =
      summaryAssets.length > 1
        ? `${t("admin.lms.tree.partSummary", "Summary Slide")} ${index + 1}`
        : t("admin.lms.tree.partSummary", "Summary Slide");
    expanded.push({
      ...node,
      key: `${node.key}-summary-${assetId(asset, index)}`,
      kind: "sectionSummary",
      short: `${partTitle} - ${summaryLabel}`,
      assets: [asset],
      quiz: null,
      quizId: null,
      optional: true,
    });
  });

  if (node.quizId || node.quiz) {
    expanded.push({
      ...node,
      key: `${node.key}-quiz`,
      kind: "sectionQuiz",
      short: `${partTitle} - ${t("learner.lms.player.quiz", "Quiz")}`,
      assets: [],
    });
  }

  return expanded.length ? expanded : [{ ...node, short: partTitle }];
};

const groupPreviewNodes = (nodes = []) => {
  const topLevel = [];
  const final = [];
  const byModule = new Map();
  const order = [];

  nodes.forEach((node) => {
    if (node.kind === "courseIntro") {
      topLevel.push(node);
      return;
    }
    if (node.kind === "finalQuiz") {
      final.push(node);
      return;
    }
    if (!byModule.has(node.moduleId)) {
      byModule.set(node.moduleId, {
        moduleId: node.moduleId,
        title: node.moduleTitle,
        num: node.moduleIndex,
        nodes: [],
      });
      order.push(node.moduleId);
    }
    byModule.get(node.moduleId).nodes.push(node);
  });

  return { topLevel, modules: order.map((id) => byModule.get(id)), final };
};

const PreviewCourseSidebar = ({
  nodes = [],
  currentKey,
  done,
  courseTitle,
  progress,
  onSelect,
  t,
}) => {
  const grouped = useMemo(() => groupPreviewNodes(nodes), [nodes]);
  const currentNode = nodes.find((node) => node.key === currentKey);
  const [openModules, setOpenModules] = useState({});

  useEffect(() => {
    const firstModuleId = grouped.modules[0]?.moduleId;
    const activeModuleId = currentNode?.moduleId || firstModuleId;
    if (!activeModuleId) return;
    setOpenModules((prev) => ({ ...prev, [activeModuleId]: true }));
  }, [currentNode?.moduleId, grouped.modules]);

  const isDone = (node) => done?.has(node.key);
  const leaf = (node) => {
    const complete = isDone(node);
    return (
      <button
        key={node.key}
        className={`lms-cc-item ${node.key === currentKey ? "active" : ""} ${complete ? "done" : ""}`}
        onClick={() => onSelect(node.key)}
        aria-current={node.key === currentKey ? "true" : undefined}
        type="button"
      >
        <i
          className={`fas ${complete ? "fa-check-circle" : previewNodeIcon(node.kind)} lms-cc-icon`}
          aria-hidden="true"
        ></i>
        <span className="lms-cc-label">{node.short || node.label}</span>
      </button>
    );
  };

  return (
    <nav
      className="lms-player-sidebar"
      aria-label={t("learner.lms.player.contents", "Course contents")}
    >
      <div className="lms-cc-head">
        <h2 className="lms-cc-title">{t("learner.lms.player.courseContent", "Course Content")}</h2>
        <p className="lms-cc-course">{courseTitle}</p>
        <span className="lms-cc-pct">
          {t("admin.lms.preview.stepCount", "Step {{step}}", {
            step: `${progress.doneCount}/${progress.total}`,
          })}
        </span>
      </div>

      {grouped.topLevel.map(leaf)}

      {grouped.modules.map((module) => {
        const open = openModules[module.moduleId] !== false;
        const complete =
          module.nodes.length > 0 && module.nodes.every((node) => isDone(node));
        return (
          <div className="lms-cc-group" key={module.moduleId}>
            <button
              className={`lms-cc-module ${complete ? "done" : ""}`}
              onClick={() =>
                setOpenModules((prev) => ({
                  ...prev,
                  [module.moduleId]: !open,
                }))
              }
              aria-expanded={open}
              type="button"
            >
              <i
                className={`fas ${complete ? "fa-check-circle" : "fa-book-open"} lms-cc-icon`}
                aria-hidden="true"
              ></i>
              <span className="lms-cc-label">
                {t("learner.lms.player.moduleNum", "Module {{n}}", { n: module.num })}
                {module.title ? `: ${module.title}` : ""}
              </span>
              <i className={`fas fa-chevron-${open ? "up" : "down"} lms-cc-caret`} aria-hidden="true"></i>
            </button>
            {open && <div className="lms-cc-children">{module.nodes.map(leaf)}</div>}
          </div>
        );
      })}

      {grouped.final.map(leaf)}
    </nav>
  );
};

const LmsCoursePreview = (props) => {
  const { t } = useTranslation();
  const courseId = props?.match?.params?.id;
  const query = new URLSearchParams(props?.location?.search || "");

  const [navbarToggle, setNavbarToggle] = useState(false);
  const [allowView, setAllowView] = useState(false);
  const [preview, setPreview] = useState(null); // server preview payload

  const [persona, setPersona] = useState(null);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [currentKey, setCurrentKey] = useState(null);
  const [done, setDone] = useState(new Set());
  const [quizOpen, setQuizOpen] = useState(false); // section/final quiz launched

  // On mount, render the shell + open the persona modal (or use query persona).
  useEffect(() => {
    setAllowView(true);
    const a = query.get("audience");
    const ar = query.get("area");
    if (a && ar) fetchPreview({ audience: a, area: ar });
    else setPersonaOpen(true);
  }, []);

  // The preview is persona-filtered server-side, so we (re)fetch per persona.
  const fetchPreview = async (p) => {
    setPersona(p);
    setPersonaOpen(false);
    setCurrentKey(null);
    setDone(new Set());
    try {
      globalLoader(true);
      const res = await lmsCoursePreviewApi(courseId, {
        serviceAudience: p.audience,
        serviceArea: p.area,
      });
      globalLoader(false);
      if (res?.data?.status === 200) setPreview(res?.data?.data || {});
      else globalAlert("error", res?.data?.message);
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message);
      if (e?.response?.data?.status === 401) logOut(props?.history, "/admin/login");
    }
  };

  const rawNodes = useMemo(() => {
    if (!preview) return [];
    const supportArea = targetPreviewServiceArea(preview);
    return buildPreviewNodes(preview).flatMap((node) => {
      if (node.kind !== "support") return [node];
      const assets = filterSupportForArea(node.assets || [], supportArea);
      return assets.length ? [{ ...node, assets }] : [];
    });
  }, [preview]);
  const nodes = useMemo(
    () => rawNodes.flatMap((previewNode) => expandPreviewNode(previewNode, t)),
    [rawNodes, t]
  );

  // Pick the starting node once nodes are ready (honour ?module / ?section).
  useEffect(() => {
    if (!persona || !nodes.length || currentKey) return;
    const qSection = query.get("section");
    const qModule = query.get("module");
    let start = nodes[0];
    if (qSection) start = nodes.find((n) => n.sectionId === qSection) || start;
    else if (qModule) start = nodes.find((n) => n.moduleId === qModule) || start;
    setCurrentKey(start.key);
  }, [persona, nodes]);

  const idx = nodes.findIndex((n) => n.key === currentKey);
  const node = idx >= 0 ? nodes[idx] : null;
  const progress = previewProgress(nodes, done);
  const courseTitle =
    preview?.course?.title ||
    preview?.course?.name ||
    t("admin.lms.preview.coursePreview", "Course preview");

  const markDone = (key) =>
    setDone((d) => {
      const n = new Set(d);
      n.add(key);
      return n;
    });
  const go = (toKey) => {
    setQuizOpen(false);
    setCurrentKey(toKey);
  };
  const goBy = (delta) => {
    const ni = idx + delta;
    if (ni >= 0 && ni < nodes.length) go(nodes[ni].key);
  };

  const returnTo =
    props?.location?.state?.returnTo || query.get("returnTo") || "";
  const fallbackStep = query.get("module") || query.get("section") ? 3 : 4;
  const exitTarget = returnTo.startsWith("/admin/lms/")
    ? returnTo
    : `/admin/lms/course/${courseId}?step=${fallbackStep}`;
  const exit = () => props?.history?.push(exitTarget);

  return (
    <>
      <Helmet>
        <title>{t("admin.lms.preview.title")} - eC-Card</title>
        <meta name="viewport" content="width=1200, initial-scale=1" />
      </Helmet>
      {allowView && (
        <div className="admin-portal">
          <Header roleType="admin" />
          <div className={`${navbarToggle ? "active" : ""} wrapper`}>
            <SidebarComponent
              navbarToggle={navbarToggle}
              roleType="admin"
              menu="lms"
              subMenu="lms-courses"
            />
            <div className="main">
              <div className="main-wrapper">
                <div>
                  <p className="admin-banner">{t("admin.lms.preview.title")}</p>
                </div>
                <SidebarArrow handleSidebarToggle={setNavbarToggle} />

                <div className="container-fluid pt-3 lms-admin-preview-page">
                  <div className="lms-player-head lms-admin-preview-head">
                    <div>
                      <h1 className="lms-portal-title mb-1">{courseTitle}</h1>
                      <div className="lms-player-meta">
                        <span className="lms-preview-mode-pill">
                          <i className="fas fa-eye"></i>
                          {t("admin.lms.preview.modeBanner")}
                        </span>
                        {persona && (
                          <span className="lms-preview-persona-chip">
                            <i className="fas fa-user-circle"></i>
                            <span>
                              {t(`admin.lms.audience.${persona.audience}`, persona.audience)}
                            </span>
                            <button
                              type="button"
                              className="lms-preview-chip-btn"
                              onClick={() => setPersonaOpen(true)}
                              title={t("admin.lms.preview.changePersona")}
                            >
                              <i className="fas fa-user-cog"></i>
                            </button>
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      className="btn btn-sm secondary-btn btn-own-grey btn-with-icon"
                      onClick={exit}
                    >
                      <i className="fas fa-times mr-1"></i>
                      {t("admin.lms.preview.exit")}
                    </button>
                  </div>

                  {persona && (
                    <div className="lms-player-shell lms-admin-preview-player-shell">
                      <PreviewCourseSidebar
                        nodes={nodes}
                        currentKey={currentKey}
                        done={done}
                        progress={progress}
                        onSelect={go}
                        courseTitle={courseTitle}
                        t={t}
                      />

                      <div className="lms-player-stage">
                        {!node && (
                          <div className="lms-player-card lms-empty-right">
                            <i className="fas fa-folder-open mb-2"></i>
                            <p className="text-muted mb-0">
                              {t("admin.lms.preview.empty")}
                            </p>
                          </div>
                        )}

                        {node && (
                          <div className="lms-player-card">
                            <NodeView
                              node={node}
                              quizOpen={quizOpen}
                              onOpenQuiz={() => setQuizOpen(true)}
                              onQuizPassed={() => markDone(node.key)}
                              onPrevious={() => goBy(-1)}
                              onNext={() => {
                                markDone(node.key);
                                goBy(1);
                              }}
                              hasPrevious={idx > 0}
                              hasNext={idx < nodes.length - 1}
                              t={t}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {personaOpen && (
        <LmsPreviewPersonaModal
          open={personaOpen}
          audience={persona?.audience}
          area={persona?.area}
          onStart={fetchPreview}
          onClose={() => setPersonaOpen(false)}
        />
      )}
    </>
  );
};

// Renders the current node: an asset screen, a focused section resource,
// or an assessment quiz.
export const NodeView = ({
  node,
  selectedResource,
  quizOpen,
  onOpenQuiz,
  onQuizPassed,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  t,
}) => {
  const moduleContext = moduleNodeTitle(node, t);
  const nodeTitle = node.short || node.sectionTitle || node.moduleTitle || node.label || "";
  const assets = node.asset ? [node.asset] : node.assets || [];
  const isQuizNode = node.kind === "sectionQuiz" || node.kind === "finalQuiz";
  const isPartNode = node.kind === "section";
  const selectedAsset =
    isPartNode && selectedResource?.type === "asset" ? selectedResource.asset : null;
  const selectedQuiz = isPartNode && selectedResource?.type === "quiz";
  const viewAssets = isPartNode ? (selectedAsset ? [selectedAsset] : []) : assets;
  const showQuizCard = isQuizNode || selectedQuiz;

  const renderQuizLaunchCard = (options = {}) => {
    const quiz = node.quiz || {};
    const questionCount = quizQuestionCount(quiz);
    const duration = quizDurationMinutes(quiz);
    const passMark = quizPassMark(quiz);
    const quizTitle =
      quiz.title ||
      options.title ||
      t("admin.lms.section.quizHeading", "Section quiz");
    const description =
      quiz.description ||
      t(
        "learner.lms.quiz.cardHint",
        "Test your understanding before proceeding."
      );

    return (
      <div className="lms-player-quizcard">
        <div className="lms-player-quizcard-main">
          <span className="lms-player-quizcard-icon" aria-hidden="true">
            <i className="fas fa-question-circle"></i>
          </span>
          <div className="lms-player-quizcard-text">
            <span className="lms-player-quizcard-title">{quizTitle}</span>
            <span className="lms-player-quizcard-sub">{description}</span>
            {(questionCount || duration || passMark) && (
              <span className="lms-admin-preview-quiz-meta">
                {questionCount && (
                  <span>
                    <i className="fas fa-list-ol"></i>
                    {questionCount}{" "}
                    {questionCount === 1
                      ? t("admin.lms.preview.question", "question")
                      : t("admin.lms.preview.questions", "questions")}
                  </span>
                )}
                {duration && (
                  <span>
                    <i className="fas fa-clock"></i>
                    {duration} {t("admin.lms.preview.minutes", "min")}
                  </span>
                )}
                {passMark && (
                  <span>
                    <i className="fas fa-bullseye"></i>
                    {t("admin.lms.preview.passMarkIs", { mark: passMark })}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
        <button className="btn lms-btn-primary" onClick={onOpenQuiz}>
          <i className="fas fa-play me-2"></i>
          {t("learner.lms.quiz.start", "Start quiz")}
        </button>
      </div>
    );
  };

  return (
    <div className="lms-preview-node-view lms-admin-preview-node-view">
      <div className="lms-player-node-head">
        <span className="lms-player-node-eyebrow">
          <i className={`fas ${previewNodeIcon(node.kind)}`} aria-hidden="true"></i>
          {moduleContext || t("learner.lms.player.courseContent", "Course Content")}
        </span>
        <h2 className="lms-player-node-title">{nodeTitle}</h2>
      </div>

      {showQuizCard ? (
        quizOpen ? (
          <LmsQuizRunner
            quizId={node.quizId}
            quiz={node.quiz}
            quizMeta={{
              moduleBadge: node.moduleIndex
                ? t("learner.lms.player.moduleNum", "Module {{n}}", { n: node.moduleIndex })
                : t("admin.lms.preview.assessment", "Assessment"),
              moduleTitle: node.moduleTitle || "",
              partTitle:
                node.kind === "finalQuiz"
                  ? t("admin.lms.preview.finalAssessment")
                  : node.short || t("admin.lms.section.quizHeading", "Section quiz"),
              quizTitle:
                node.quiz?.title ||
                (node.kind === "finalQuiz"
                  ? t("admin.lms.preview.finalAssessment")
                  : t("admin.lms.section.quizHeading", "Section quiz")),
            }}
            onComplete={onQuizPassed}
          />
        ) : (
          renderQuizLaunchCard({
            title:
              node.kind === "finalQuiz"
                ? t("admin.lms.preview.finalAssessment")
                : t("admin.lms.section.quizHeading", "Section quiz"),
          })
        )
      ) : (
        <>
          {isPartNode && !selectedResource && (
            <div className="lms-preview-missing">
              <i className="fas fa-hand-pointer"></i>
              {t(
                "admin.lms.preview.selectPartResource",
                "Select a resource inside this Part to preview it."
              )}
            </div>
          )}
          {!isPartNode && viewAssets.length === 0 && (
            <div className="lms-preview-missing">
              <i className="fas fa-file-circle-xmark"></i>
              {t("admin.lms.preview.noAssets")}
            </div>
          )}
          {viewAssets.map((asset, index) => (
            <LmsAssetRenderer asset={asset} key={assetId(asset, index)} />
          ))}
        </>
      )}

      <div className="lms-player-nav">
        <button 
          className="btn lms-btn-primary" 
          disabled={!hasPrevious} 
          onClick={onPrevious}
          style={!hasPrevious ? { cursor: 'not-allowed' } : {}}
        >
          <i className="fas fa-arrow-left me-2"></i>
          {t("learner.lms.player.previous", "Previous")}
        </button>
        <button 
          className="btn lms-btn-primary" 
          disabled={!hasNext} 
          onClick={onNext}
          style={!hasNext ? { cursor: 'not-allowed' } : {}}
        >
          {t("learner.lms.player.next", "Next")}
          <i className="fas fa-arrow-right mx-2"></i>
        </button>
      </div>
    </div>
  );
};

export default withRouter(LmsCoursePreview);
